import { useEffect, useState } from 'react';

export type VisualViewportRect = {
  // null until measured / when the API is unavailable — callers fall back to
  // 100dvh sizing in that case.
  height: number | null;
  offsetTop: number;
};

// Tracks `window.visualViewport` so a mobile layout can pin itself to the
// region that's actually visible above the soft keyboard. On iOS Safari the
// keyboard shrinks the *visual* viewport but not the layout viewport, so a
// `100dvh`/`100vh` panel stays full-height and the browser scrolls the focused
// input into view — which pushes the panel (and its header) off the top. We
// read the live visual-viewport height + offsetTop instead and size the panel
// to it, so the header stays pinned at the top of what's visible.
//
// Gated by `enabled` (mobile only); when disabled or when the API is missing
// it returns `{ height: null, offsetTop: 0 }` and the caller uses its dvh
// fallback — desktop is never affected.
export function useVisualViewport(enabled: boolean): VisualViewportRect {
  const [rect, setRect] = useState<VisualViewportRect>(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!enabled || !vv) return { height: null, offsetTop: 0 };
    return { height: vv.height, offsetTop: vv.offsetTop };
  });

  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!enabled || !vv) {
      setRect({ height: null, offsetTop: 0 });
      return;
    }
    const update = () => setRect({ height: vv.height, offsetTop: vv.offsetTop });
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [enabled]);

  return rect;
}
